import { Loader2, Mail, Settings } from 'lucide-react';

/**
 * Shows when the personal inbox is empty.
 * - If at least one platform is connected: shows ONE sync button.
 * - If no platform is connected: shows a link to Settings.
 *
 * @param {boolean} gmailConnected - whether Gmail OAuth is connected
 * @param {boolean} outlookConnected - whether Outlook OAuth is connected
 * @param {boolean} isSyncing - whether sync is in progress
 * @param {function} onSync - called when the sync button is clicked
 * @param {function} onGoToSettings - called when the settings button is clicked
 */
export default function SyncInboxPrompt({
    gmailConnected,
    outlookConnected,
    isSyncing,
    onSync,
    onGoToSettings,
}) {
    const anyConnected = gmailConnected || outlookConnected;

    // --- No platforms connected ---
    if (!anyConnected) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">
                        Your inbox is empty
                    </h3>
                    <p className="text-sm text-stone-500 mb-6">
                        Connect your email account to see all your emails here.
                    </p>
                    <button
                        onClick={onGoToSettings}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                        <Settings className="w-4 h-4" />
                        Go to Settings
                    </button>
                </div>
            </div>
        );
    }

    // --- At least one platform connected ---
    const label =
        gmailConnected && outlookConnected
            ? 'Sync Gmail & Outlook'
            : gmailConnected
              ? 'Sync Gmail'
              : 'Sync Outlook';

    return (
        <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
                <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700 mb-2">
                    No mail yet
                </h3>
                <p className="text-sm text-stone-500 mb-6">
                    We synced your mailbox but found no messages. Try syncing again, or check your filters.
                </p>
                <button
                    onClick={onSync}
                    disabled={isSyncing}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isSyncing ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Syncing...
                        </>
                    ) : (
                        <>
                            <Mail className="w-4 h-4" />
                            {label}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
